import type { CostDateField, SlotDateField } from 'components/nri-table/types';
import type { ToolStatus } from './types';

/**
 * Reads a staging tool's result into the change it describes.
 *
 * Both evaluation pages receive the same tool payloads and differ only in how
 * they APPLY a change — one writes working atoms, the other appends a slot
 * override operation. The parsing and the guards are identical, and getting a
 * guard wrong is silent: the agent reports "Staged", nothing lands on the page,
 * and the user presses Calculate expecting an edit that was never made. That has
 * happened once already (the tool payload's `success` key was being stripped, so
 * a handler guarding on it always bailed), which is why this lives in one tested
 * place rather than twice inline.
 */

/**
 * Cost-date keys as the tool emits them, mapped to the page's field names.
 *
 * Typed as the grid's own `CostDateField` rather than inferred from this literal:
 * inferring re-declared a second type of the same name in the codebase, so an import
 * from the wrong module type-checked and would have drifted silently. Annotating
 * instead makes the compiler prove this map covers exactly the grid's fields.
 */
const COST_DATE_FIELDS: Record<string, CostDateField> = {
  drilling_costs_date: 'drillingCostsDate',
  completion_costs_date: 'completionCostsDate',
  facilities_costs_date: 'facilitiesCostsDate',
  contingencies_costs_date: 'contingenciesCostsDate',
  other_costs_date: 'otherCostsDate',
};

/**
 * Common to every staged change: the run the tool staged it against. The tool
 * resolves that from the request context or an explicit `calculation_id`, and a
 * conversation outlives the page's selection — so a result can arrive after the user
 * has switched runs. Applying it then would put the edit on a run they are no longer
 * looking at, so the host compares this against its open run and refuses a mismatch.
 */
interface StagedChangeBase {
  /** Null when the tool did not report one; the host then cannot verify and skips. */
  calculationId: number | null;
}

export interface StagedAfeChange extends StagedChangeBase {
  kind: 'afe';
  slotIds: string[];
  afeSourceId: string;
  /**
   * BE-derived column the FE cannot recompute; rides along to keep tables aligned.
   *
   * Explicitly nullable, and always present: `afeSourceId` changes in the same update, so
   * an absent well name has to CLEAR the previous AFE's name rather than leave it behind
   * labelling the new source. `null` means "the tool resolved no name — clear it", which
   * is a different instruction from "unknown, leave alone" and the two must not collapse.
   */
  afeWellName?: string | null;
  /**
   * The AFE Source cell links through this. Same contract as `volumeSourceHref`: an entry
   * that omits it leaves the row on its previous href, so the cell names the new AFE while
   * the link still points at the well behind the old one — the run 1874 defect, on the
   * column that never got the fix.
   */
  afeSourceHref?: string | null;
  costDates: Partial<Record<CostDateField, string>>;
}

export interface StagedVolumeChange extends StagedChangeBase {
  kind: 'volume';
  slotIds: string[];
  volumeSourceId: string;
  volumeSourceType?: string;
  /**
   * The grid links the Volume Source cell through this href. An entry that omits it
   * leaves the row on its BASE href, so the cell names the NEW curve while the link
   * still points at the one it just left — reproduced on run 1874, slot 3002554029.
   * The pickers send it; the agent path dropped it here, so an agent-staged move was
   * persisted without it and reproduced the same bug it was fixed for.
   */
  volumeSourceHref?: string;
  forecastSource?: string;
  dcaScenario?: string;
}

/**
 * Schedule dates the Bulk Edit Slots panel exposes, in the grid's field names.
 * Typed as the grid's `SlotDateField` for the same reason as COST_DATE_FIELDS above.
 */
const SCHEDULE_DATE_FIELDS: Record<string, SlotDateField> = {
  permit_date: 'permitDate',
  spud_date: 'spudDate',
  first_production_date: 'firstProductionDate',
};

export interface StagedScheduleChange extends StagedChangeBase {
  kind: 'schedule';
  slotIds: string[];
  /**
   * The dates are identical for every property in one call, so the tool sends them
   * once and they are expanded across `slotIds` here. Note these cascade: the AFE
   * cost dates default off spud and first production, so the calculator will move
   * those too unless a cost date was overridden explicitly.
   */
  dates: Partial<Record<SlotDateField, string>>;
}

export type StagedSettingsField =
  'scenarioId' | 'dcaScenarioId' | 'evaluationCaseId';

export interface StagedSettingsChange extends StagedChangeBase {
  kind: 'settings';
  /**
   * Ordered, and the order is load-bearing: selecting an evaluation case also
   * clears the run's per-area overrides and its explicit price-deck pick, so it
   * must be applied LAST or a scenario staged in the same call is wiped.
   */
  operations: Array<{ field: StagedSettingsField; value: number }>;
}

export type StagedChange =
  | StagedAfeChange
  | StagedVolumeChange
  | StagedScheduleChange
  | StagedSettingsChange
  | null;

interface AfePayload {
  staged?: boolean;
  calculation_id?: number | null;
  afe?: {
    id: number;
    well_name?: string | null;
    well_href?: string | null;
  } | null;
  /**
   * The instruction to apply. The AFE and cost dates are sent ONCE and expanded
   * across `slot_ids` here — repeating them per slot pushed a package-wide change
   * past the display cap, which discarded the payload and left nothing to apply.
   */
  staged_change?: {
    kind?: string;
    afe_header_id?: number;
    cost_dates?: Record<string, string> | null;
    slot_ids?: string[] | null;
  } | null;
}

interface VolumePayload {
  staged?: boolean;
  calculation_id?: number | null;
  staged_change?: {
    kind?: string;
    volume_source_id?: string;
    volume_source_type?: string;
    volume_source_href?: string;
    forecast_source?: string;
    dca_scenario?: string;
    is_user_modified?: boolean;
    slot_ids?: string[] | null;
  } | null;
}

interface SchedulePayload {
  staged?: boolean;
  calculation_id?: number | null;
  staged_change?: {
    kind?: string;
    dates?: Record<string, string> | null;
    slot_ids?: string[] | null;
  } | null;
}

interface SettingsPayload {
  staged?: boolean;
  calculation_id?: number | null;
  scenario?: { id: number } | null;
  evaluation_case?: { id: number } | null;
  dca_scenario?: { id: number } | null;
}

/**
 * The slot ids from a staging instruction, checked at runtime rather than trusted.
 *
 * This is the one field in this file that was trusted structurally: `slot_ids` is typed
 * `string[] | null` and reached through an `as` cast, and TypeScript erases at runtime, so
 * a bare string passed straight through. `"SLOT-A".length === 7` cleared the emptiness
 * guard, and then the two pages failed differently and both silently — eval-3 threw inside
 * `mergeBySlot` into a `console.error`, with `unappliedStageWarning` never running because
 * the parse had SUCCEEDED, so the transcript said "Staged" and not one user-visible word
 * appeared; eval3-new built a Set of six characters, matched nothing, and put a string on
 * the Calculate wire where an array belongs.
 *
 * Guarding at the seam covers both pages and any future host. This is robustness rather
 * than a security boundary — the payload is assembled by the Ruby tool, not written by the
 * model — but the drift is reachable from our own side, which is why it is worth a runtime
 * check: the AFE tool maps `unique_slot_id` out of a JSON blob without the `.to_s` its own
 * `selection_slot_ids` applies. Dropping non-strings also disposes of nulls, which used to
 * write a working-map key of `"null"`.
 *
 * @param {object} instruction - The `staged_change` block from a staging tool's payload.
 * @return {string[]} The usable slot ids, empty when the field is not an array of them.
 */
function readSlotIds(
  instruction: { slot_ids?: unknown } | null | undefined
): string[] {
  if (!Array.isArray(instruction?.slot_ids)) return [];

  return instruction.slot_ids.filter(
    (id): id is string => typeof id === 'string' && id.length > 0
  );
}

function readAfe(output: unknown): StagedAfeChange | null {
  const result = output as AfePayload | null;
  // `staged` distinguishes a real change from a selection that matched nothing.
  if (!result?.staged || !result.afe?.id) return null;

  const instruction = result.staged_change;
  const slotIds = readSlotIds(instruction);
  if (slotIds.length === 0) return null;

  const emitted = instruction?.cost_dates ?? {};
  const costDates: Partial<Record<CostDateField, string>> = {};
  Object.entries(COST_DATE_FIELDS).forEach(([toolKey, field]) => {
    const value = emitted[toolKey];
    if (value) costDates[field] = value;
  });

  return {
    kind: 'afe',
    calculationId: result.calculation_id ?? null,
    slotIds,
    afeSourceId: String(result.afe.id),
    // Written even when the tool resolved nothing, unlike the volume descriptors below:
    // the AFE changed in this same instruction, so a missing name or href is an
    // instruction to clear — keeping the old one would label the new AFE with the
    // displaced AFE's well and link to it.
    afeWellName: result.afe.well_name ?? null,
    afeSourceHref: result.afe.well_href ?? null,
    costDates,
  };
}

function readVolume(output: unknown): StagedVolumeChange | null {
  const result = output as VolumePayload | null;
  if (!result?.staged) return null;

  const instruction = result.staged_change;
  const slotIds = readSlotIds(instruction);
  if (slotIds.length === 0 || !instruction?.volume_source_id) return null;

  // One forecast per call, so its descriptors are sent once and applied to every
  // slot. A PDP forecast belongs to one well, so multi-slot PDP selections are
  // refused server-side rather than expanded here.
  return {
    kind: 'volume',
    calculationId: result.calculation_id ?? null,
    slotIds,
    volumeSourceId: instruction.volume_source_id,
    ...(instruction.volume_source_type
      ? { volumeSourceType: instruction.volume_source_type }
      : {}),
    ...(instruction.volume_source_href
      ? { volumeSourceHref: instruction.volume_source_href }
      : {}),
    ...(instruction.forecast_source
      ? { forecastSource: instruction.forecast_source }
      : {}),
    ...(instruction.dca_scenario
      ? { dcaScenario: instruction.dca_scenario }
      : {}),
  };
}

function readSchedule(output: unknown): StagedScheduleChange | null {
  const result = output as SchedulePayload | null;
  if (!result?.staged) return null;

  const instruction = result.staged_change;
  const slotIds = readSlotIds(instruction);
  if (slotIds.length === 0) return null;

  const emitted = instruction?.dates ?? {};
  const dates: Partial<Record<SlotDateField, string>> = {};
  Object.entries(SCHEDULE_DATE_FIELDS).forEach(([toolKey, field]) => {
    const value = emitted[toolKey];
    if (value) dates[field] = value;
  });
  if (Object.keys(dates).length === 0) return null;

  return {
    kind: 'schedule',
    calculationId: result.calculation_id ?? null,
    slotIds,
    dates,
  };
}

function readSettings(output: unknown): StagedSettingsChange | null {
  const result = output as SettingsPayload | null;
  if (!result?.staged) return null;

  const operations: StagedSettingsChange['operations'] = [];
  if (result.scenario?.id) {
    operations.push({ field: 'scenarioId', value: result.scenario.id });
  }
  if (result.dca_scenario?.id) {
    operations.push({ field: 'dcaScenarioId', value: result.dca_scenario.id });
  }
  // Last, deliberately — see `operations` above.
  if (result.evaluation_case?.id) {
    operations.push({
      field: 'evaluationCaseId',
      value: result.evaluation_case.id,
    });
  }

  if (operations.length === 0) return null;

  return {
    kind: 'settings',
    calculationId: result.calculation_id ?? null,
    operations,
  };
}

/**
 * @param {string | null} name - The tool that produced this result.
 * @param {unknown} output - Its parsed payload.
 * @param {ToolStatus} status - Whether the tool call succeeded. The tool payload
 *   carries no `success` key, so this is the only authority on that.
 * @return {StagedChange} The change to apply, or null when there is nothing to
 *   apply.
 */
export function readStagedChange(
  name: string | null,
  output: unknown,
  status: ToolStatus
): StagedChange {
  if (status !== 'success') return null;

  switch (name) {
    case 'eval_stage_afe_change':
      return readAfe(output);
    case 'eval_stage_volume_change':
      return readVolume(output);
    case 'eval_stage_schedule_change':
      return readSchedule(output);
    case 'eval_stage_settings_change':
      return readSettings(output);
    default:
      return null;
  }
}

/**
 * Whether a staged change may be applied to the run the page currently has open, and
 * what to tell the user when it may not.
 *
 * A conversation outlives the page's run selection, so a result can land after the user
 * has switched runs — or on a route with no run open at all, where the agent stays
 * mounted and the server falls back to the workspace's latest completed calculation.
 * Accepting that fallback committed an edit into no-run working state and let the
 * assistant confirm a stage against a run the user was not looking at.
 *
 * So the rule is an exact match on a run that actually is open: anything else refuses.
 * Refusing SILENTLY would recreate the same failure from the other side (the assistant
 * says staged, nothing is), which is why this returns the sentence rather than a boolean.
 *
 * @param {number | null} changeCalculationId - The run the tool staged against.
 * @param {number | null} openCalculationId - The run the page has open.
 * @return {string | null} Null when it is safe to apply; otherwise what to show.
 */
export function stagedChangeRefusal(
  changeCalculationId: number | null,
  openCalculationId: number | null
): string | null {
  if (openCalculationId === null) {
    return 'That change was staged, but no evaluation is open on this page, so it was not applied. Open the run you want it on and ask again.';
  }

  if (changeCalculationId === null) {
    return `That change did not say which evaluation it was staged against, so it was not applied to ${openCalculationId}. Ask again with the run open.`;
  }

  if (changeCalculationId !== openCalculationId) {
    return `That change was staged against evaluation ${changeCalculationId}, but ${openCalculationId} is now open — it was not applied. Ask again to stage it here.`;
  }

  return null;
}

/**
 * Apply a staged settings change through the host's own setters.
 *
 * Both eval pages ran the same ordered `forEach` over the same three fields, differing
 * only in which setter each field reached — working atoms on one page, the draft form on
 * the other. As a chain of `if (field === …)` a fourth field would have been silently
 * ignored by both; as a keyed record, TypeScript makes adding one a compile error at
 * every call site.
 *
 * Order is preserved from `operations`, and it is load-bearing — see StagedSettingsChange.
 *
 * @param {StagedSettingsChange} change - The staged settings change to apply.
 * @param {Record<StagedSettingsField, Function>} setters - Host setter per field.
 * @return {void}
 */
export function applyStagedSettings(
  change: StagedSettingsChange,
  setters: Record<StagedSettingsField, (value: number) => void>
): void {
  change.operations.forEach(({ field, value }) => setters[field](value));
}

/** Tool-name prefix every staging tool shares — the registry key, minus the `_tool` suffix. */
const STAGING_TOOL_PREFIX = 'eval_stage_';

/**
 * Detects the one failure this whole seam exists to prevent: the tool reported a change
 * as STAGED, and parsing it produced nothing to apply.
 *
 * `readStagedChange` returns null for a great many legitimate reasons — a read tool, a
 * selection that matched nothing, a refusal — and none of those deserve a warning: the
 * assistant already tells the user in words. The anomaly is narrower and worth shouting
 * about: `staged: true` came back, so the transcript now says the change is queued, and
 * yet nothing reached the page. The user presses Calculate on numbers that never moved.
 *
 * This went unnoticed through several rounds of UAT precisely because the handler's
 * `if (!change) return` was the only exit that said nothing. Every other one notifies.
 *
 * @param {string | null} name - The tool that produced this result.
 * @param {unknown} output - Its parsed payload.
 * @param {ToolStatus} status - Whether the tool call succeeded.
 * @return {string | null} A sentence to show the user, or null when nothing is wrong.
 */
export function unappliedStageWarning(
  name: string | null,
  output: unknown,
  status: ToolStatus
): string | null {
  if (!name?.startsWith(STAGING_TOOL_PREFIX)) return null;
  if (status !== 'success') return null;

  const claimed = (output as { staged?: boolean } | null)?.staged === true;
  if (!claimed) return null;

  // Re-parse rather than trusting the caller to have checked: this way the function is
  // true standing alone — "claimed a stage AND nothing came of it" — instead of only
  // correct at one call site. Parsing is a pure read of a small object.
  if (readStagedChange(name, output, status) !== null) return null;

  return `The assistant reported staging a change (${name}), but nothing was queued on this page — so pressing Calculate would not apply it. Ask it to stage the change again.`;
}
